/**
 * One role, across the trimester — read-only.
 *
 * This is the demand board's row with everything that COLLECTS demand taken out:
 * no drag handle, no team picker, no remove, no month you can click to move a
 * start date. Tracking answers a different question — was the seat filled, and
 * was it filled when it was promised — and a control that changes the plan does
 * not belong in the answer.
 *
 * WHAT IS KEPT IS THE TIMELINE, unchanged: `roleSpan` still decides where a bar
 * starts and ends, so a seat that landed on plan is a dot, one that landed late
 * is a green span from promise to arrival, an empty overdue seat is an
 * open-ended red bar with an arrowhead at the board's edge, and an on-hold role
 * is never drawn late because it was paused on purpose.
 *
 * THE HIRE'S NAME RIDES THE TIMELINE rather than taking a column: the dot's own
 * column is the start month, so the name sits beside it as a label on the event.
 * It overflows its cell deliberately — the months after somebody starts are
 * empty on that row, which is exactly the room it borrows.
 */
import type { BoardRole, DemandMonth } from '../../../../types/taDemand';
import { isDraft } from '../../../../types/taDemand';
import type { Track } from '../../demand/demandModel';
import {
  clampOpenEndedToToday,
  hasBar,
  roleSpan,
  spanCovers,
  trackMonthKeys,
} from '../../demand/demandModel';
import { RoleMarker, zoneClass } from '../../demand/demandUi';
import { indentPx } from '../../demand/demandGrid';
import { LevelBadge, ReasonGlyph, RoleHoverCard, shortDate } from './HiringRowMeta';
import { titleTone } from './hiringScope';

export function HiringRow({
  role,
  tracks,
  months,
  currentMonth,
  depth,
  cancelled = false,
}: {
  role: BoardRole;
  tracks: Track[];
  months: DemandMonth[];
  currentMonth: string;
  depth: number;
  cancelled?: boolean;
}) {
  /*
    ⚠️ CLAMPED. An overdue seat's red line is how long it has been empty, so it
    ends at today — `roleSpan` would run it on to a re-planned future date, and
    a bar drawn through months in which nothing has happened yet reads as a
    longer wait than there has been.
  */
  const span = clampOpenEndedToToday(roleSpan(role, months, currentMonth), currentMonth);
  const bar = hasBar(span);
  const filled = !isDraft(role) && role.status === 'hired';
  const late = span.months > 0;
  /*
    Whether the bar's end is a column on screen. After the clamp it almost
    always is — which is the point: the caret at the BOARD's right edge may only
    mean "this runs on past the last column", and with the bar ending at today
    it was rendering beside March while the line stopped in September.
  */
  const endsOnBoard = tracks.some((t) => trackMonthKeys(t).includes(span.to));
  const hire = isDraft(role) ? null : role.hire;
  const startLabel = hire?.startDate ? shortDate(hire.startDate) : role.actualMonth || '';

  return (
    <div
      className="group relative grid items-center border-b border-gray-100 hover:bg-gray-50"
      style={{ gridTemplateColumns: 'var(--demand-tmpl)' }}
    >
      {/*
        ⚠️ `group-hover:z-30` IS FOR THE HOVER CARD, not for this cell.
        The card lives inside here (see below) and `z-[2]` makes this cell its
        own stacking context — so at rest the card would be painted UNDER the
        next row's sticky cell, which is also `z-[2]` and comes later in the
        DOM. Lifting the hovered cell lifts the card with it.
      */}
      <div
        className="sticky left-0 z-[2] flex min-w-0 items-center gap-1.5 border-r border-gray-200 bg-white pr-2 group-hover:z-30 group-hover:bg-gray-50"
        style={{ paddingLeft: indentPx(depth) }}
      >
        <span
          className={`min-w-0 truncate text-[13px] ${titleTone(role, cancelled)} ${cancelled ? 'line-through' : ''}`}
          title={role.title}
        >
          {role.title}
        </span>
        <LevelBadge level={role.level} />
        <ReasonGlyph role={role} />
        <span className="flex-1" />
        {!cancelled && !filled && (
          <span className="shrink-0 text-[11px] leading-4 text-gray-400">
            {isDraft(role) ? 'draft' : role.status}
          </span>
        )}

        {/*
          ⚠️ THE CARD HANGS OFF THE FROZEN COLUMN, not off the row.
          It used to be a child of the row, which is as wide as all fifteen
          months — so `left-2` meant "two units from the board's left edge", and
          the moment you scrolled right the card was drawn off-screen to the
          left and arrived half visible. This cell is `sticky left-0`, so it is
          pinned to the viewport's left edge at every scroll position and the
          card is always fully on screen.
        */}
        <RoleHoverCard role={role} />
      </div>

      {tracks.map((t, i) => {
        const keys = trackMonthKeys(t);
        const here = keys.some((k) => k === span.to) && !span.openEnded;
        const covered = bar && keys.some((k) => spanCovers(span, k));
        const barStarts = keys.some((k) => k === span.from);
        const barEnds = keys.some((k) => k === span.to);
        const runsOffRight = i === tracks.length - 1;
        return (
          <div key={i} className={`relative flex items-center justify-center ${zoneClass(t)}`}>
            {covered && (
              <span
                aria-hidden
                className={`pointer-events-none absolute top-1/2 h-[3px] -translate-y-1/2 ${
                  filled ? 'bg-emerald-500' : span.openEnded ? 'bg-rose-400' : late ? 'bg-amber-400' : 'bg-gray-300'
                } ${barStarts ? 'left-1/2 rounded-l-full' : 'left-0'} ${
                  barEnds ? 'right-1/2 rounded-r-full' : 'right-0'
                }`}
              />
            )}
            {/*
              The "still running" caret sits at the END OF THE BAR, which is
              today — it used to sit at the board's right edge, where it claimed
              the wait continued through months that have not happened. Dropped
              on the last track, where the bar genuinely does run off.
            */}
            {span.openEnded && barEnds && (
              <span
                aria-hidden
                className="pointer-events-none absolute left-1/2 top-1/2 ml-1 -translate-y-1/2 text-[10px] font-bold leading-none text-rose-500"
              >
                ›
              </span>
            )}
            {/*
              `covered` too: the caret means "the bar continues past this edge",
              which requires a bar to BE at this edge. Under a narrowed scope a
              role whose span sits entirely outside the visible months has no
              bar drawn at all, and without this it still got an arrow — at July
              on a T1 view, for a seat running August to September.
            */}
            {runsOffRight && span.openEnded && !endsOnBoard && covered && (
              <span
                aria-hidden
                className="pointer-events-none absolute right-0.5 top-1/2 -translate-y-1/2 text-[10px] font-bold leading-none text-rose-500"
              >
                ›
              </span>
            )}
            {here && <RoleMarker role={role} />}
            {!here && barStarts && span.openEnded && (
              <span
                className="relative grid h-5 min-w-5 place-items-center rounded-full border-2 border-dashed border-rose-400 px-1 text-[10px] font-bold tabular-nums text-rose-500"
                title={`Planned for ${span.from} — still not started`}
              >
                {role.count > 1 ? role.count : '·'}
              </span>
            )}
            {/*
              The label for a landed hire, borrowing the empty months to its
              right. `pointer-events-none` so it can never swallow a hover that
              belongs to the row, and it is hidden on the last track, where
              there is nothing to its right to borrow.
            */}
            {here && filled && hire?.name && !runsOffRight && (
              <span className="pointer-events-none absolute left-[calc(50%+12px)] top-1/2 -translate-y-1/2 whitespace-nowrap text-[11px] leading-4 text-gray-500">
                {hire.name}
                {startLabel ? ` · ${startLabel}` : ''}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
